import { Controller } from "@hotwired/stimulus";

// www/login's single email-or-phone field (task 4864). One input, one form:
// detectFormat() watches what's typed and swaps the form's target (email vs
// phone login) and the field's name attribute so the same field posts to
// whichever backend endpoint fits — no tabs/panels to keep in sync, no
// manual "use phone instead" switch. Also drives the resend-code cooldown
// countdown on the code-entry screen. Plain Stimulus, not Turbo
// Streams/Vue — layouts/login.html.haml sets data-turbo="false" on <body>,
// so neither ever mounts on auth pages.
export default class extends Controller {
  static targets = [
    "unifiedForm",
    "unifiedInput",
    "unifiedIcon",
    "resendButton",
  ];
  static values = {
    emailPath: String,
    phonePath: String,
    resendCooldown: { type: Number, default: 0 },
  };

  connect() {
    if (this.hasResendButtonTarget && this.resendCooldownValue > 0) {
      this.startResendCooldown(this.resendCooldownValue);
    }
  }

  disconnect() {
    if (this.cooldownTimer) clearInterval(this.cooldownTimer);
  }

  // Bound to input->phone-login#detectFormat, only wired up in the view
  // when phone login is enabled — with it off the field just stays a plain
  // email field, same as before this feature existed.
  detectFormat() {
    if (!this.hasUnifiedInputTarget || !this.hasUnifiedFormTarget) return;

    const isPhone = this.looksLikePhone(this.unifiedInputTarget.value);
    this.unifiedFormTarget.action = isPhone
      ? this.phonePathValue
      : this.emailPathValue;
    this.unifiedInputTarget.name = isPhone ? "phone" : "email";

    if (this.hasUnifiedIconTarget) {
      this.unifiedIconTarget.classList.toggle("mobile", isPhone);
      this.unifiedIconTarget.classList.toggle("alternate", isPhone);
      this.unifiedIconTarget.classList.toggle("user", !isPhone);
    }
  }

  // Phone-shaped: only digits and common phone punctuation, at least one
  // digit, and no "@" — the moment a letter or "@" shows up, it's an email.
  // Empty/ambiguous input defaults to email, matching the pre-detection
  // default this field always had.
  looksLikePhone(value) {
    const trimmed = value.trim();
    if (!trimmed || trimmed.includes("@")) return false;

    const digitsOnly = trimmed.replace(/[\s().+-]/g, "");
    return digitsOnly.length > 0 && /^\d+$/.test(digitsOnly);
  }

  startResendCooldown(seconds) {
    let remaining = seconds;
    this.resendButtonTarget.disabled = true;
    this.updateResendLabel(remaining);

    this.cooldownTimer = setInterval(() => {
      remaining -= 1;
      if (remaining <= 0) {
        clearInterval(this.cooldownTimer);
        this.resendButtonTarget.disabled = false;
        this.resendButtonTarget.textContent = "Resend code";
      } else {
        this.updateResendLabel(remaining);
      }
    }, 1000);
  }

  updateResendLabel(remaining) {
    this.resendButtonTarget.textContent = `Resend code in ${remaining}s`;
  }
}
